import { useSyncExternalStore } from "react";
import { formatClock } from "./format";

/**
 * A janela de manutenção DELIBERADA, do lado do escritório (TUT-145 fatia 1e).
 *
 * **Não confundir com `lib/maintenance.ts`.** Aquele é a TUT-121: reativo,
 * entra sozinho quando a API cai e sai quando ela volta. Este é o caso oposto —
 * o sistema está de pé, o banco responde, e alguém congelou uma parte de
 * propósito. As duas convivem, e não colidem por desenho: a sonda `/api/health`
 * é ISENTA do bloqueio, então um 503 da guarda deliberada não faz a tela
 * reativa aparecer.
 *
 * ## De onde vem a informação, e por que não há uma segunda chamada
 *
 * O corpo do 503 da guarda já carrega nível, início, previsão e observação. A
 * fatia 1b deixou isso pronto de propósito, com esta frase no código: *"o corpo
 * carrega o nível e o início para o aviso não precisar de uma segunda
 * chamada"*.
 *
 * Consultar um endpoint próprio seria pior de três jeitos: mais uma requisição
 * por tela, um estado que pode divergir do que a guarda decidiu no mesmo
 * instante, e — o pior — uma consulta que **também seria bloqueada** no nível
 * `FULL`, deixando o aviso mudo justamente quando ele mais importa.
 *
 * ## Por que o aviso não se apaga sozinho
 *
 * Ele some quando uma requisição volta a passar, não por tempo. Sumir por
 * tempo faria o aviso desaparecer com a manutenção ainda ativa, e a pessoa
 * voltaria a tentar salvar — que é exatamente o que ele existe para evitar.
 */

export type NivelDeManutencao = "STORAGE" | "READ_ONLY" | "FULL";

export interface JanelaDeManutencao {
  level: NivelDeManutencao;
  startedAt: string;
  previsaoDeTermino: string | null;
  observacao: string | null;
}

let janela: JanelaDeManutencao | null = null;
const ouvintes = new Set<() => void>();

function notificar() {
  for (const ouvinte of ouvintes) ouvinte();
}

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

function ler(): JanelaDeManutencao | null {
  return janela;
}

/**
 * Registra a janela que a API acusou num 503.
 *
 * Aceita `unknown` porque quem chama é o tratamento de erro do `request()`, que
 * lida com um corpo de resposta arbitrário. Validar aqui é o que impede um 503
 * de outra origem — um proxy, por exemplo — de acender o aviso de manutenção
 * sem que haja manutenção nenhuma.
 */
export function registrarJanela(corpo: unknown): void {
  const nova = interpretarJanela(corpo);
  if (!nova) return;
  janela = nova;
  notificar();
}

/** Uma requisição passou: a janela acabou (ou nunca alcançou esta tela). */
export function limparJanela(): void {
  if (janela === null) return;
  janela = null;
  notificar();
}

const NIVEIS = new Set<NivelDeManutencao>(["STORAGE", "READ_ONLY", "FULL"]);

/**
 * Extrai a janela do corpo do 503, ou devolve nulo.
 *
 * Exportada para teste: é a fronteira entre "a API disse que há manutenção" e
 * "alguma coisa respondeu 503". Confundir as duas acenderia um aviso falso, e
 * aviso falso queima a confiança no aviso verdadeiro.
 */
export function interpretarJanela(corpo: unknown): JanelaDeManutencao | null {
  if (typeof corpo !== "object" || corpo === null) return null;
  const bruto = (corpo as { maintenance?: unknown }).maintenance;
  if (typeof bruto !== "object" || bruto === null) return null;
  const dados = bruto as Record<string, unknown>;
  const level = dados.level;
  const startedAt = dados.startedAt;
  // O nível vem da lista fechada. Um valor fora dela só pode ser corpo forjado
  // ou versão de API mais nova que esta tela — e nos dois casos inventar um
  // texto seria pior que não mostrar aviso nenhum.
  if (typeof level !== "string" || !NIVEIS.has(level as NivelDeManutencao)) return null;
  if (typeof startedAt !== "string") return null;
  return {
    level: level as NivelDeManutencao,
    startedAt,
    previsaoDeTermino:
      typeof dados.previsaoDeTermino === "string" ? dados.previsaoDeTermino : null,
    observacao: typeof dados.observacao === "string" ? dados.observacao : null,
  };
}

export function useJanelaDeManutencao(): JanelaDeManutencao | null {
  return useSyncExternalStore(assinar, ler, ler);
}

/**
 * O que está parado, em uma frase, na língua de quem usa.
 *
 * O texto fala do EFEITO, não do nível: `READ_ONLY` não significa nada para
 * quem está tentando salvar um laudo. Dizer "o sistema está só para leitura"
 * responde à pergunta que a pessoa realmente tem — *"por que não salvou?"*.
 */
export function descreverNivel(level: NivelDeManutencao): string {
  switch (level) {
    case "STORAGE":
      return "Enviar e baixar documento está indisponível. O resto do sistema continua funcionando normalmente.";
    case "READ_ONLY":
      return "O sistema está somente para leitura: nada pode ser salvo agora. Consultar continua funcionando.";
    case "FULL":
      return "O sistema está temporariamente indisponível.";
  }
}

/**
 * A previsão de término, e o que fazer quando ela vence.
 *
 * **A previsão vencida é dita em voz alta**, exatamente como no Console. Uma
 * previsão sozinha vira mentira em vinte minutos: você promete 14:30, passa uma
 * hora, e a tela continua prometendo. O escritório deixa de confiar no aviso —
 * e um aviso em que ninguém confia contamina o **seguinte**, que é o dano que
 * não se desfaz.
 *
 * O instante EXATO da previsão ainda conta como dentro do prazo: errar essa
 * fronteira faz a tela acusar atraso um segundo cedo demais.
 */
export function descreverPrevisao(
  janelaAtual: JanelaDeManutencao,
  agora: Date,
): string {
  if (!janelaAtual.previsaoDeTermino) {
    // Sem previsão é um estado legítimo — numa emergência ninguém sabe quanto
    // vai levar. Deixar em branco pareceria informação faltando.
    return "Sem previsão de término.";
  }
  const previsto = new Date(janelaAtual.previsaoDeTermino);
  const hora = formatClock(previsto);
  if (agora.getTime() > previsto.getTime()) {
    return `A previsão de ${hora} já passou.`;
  }
  return `Previsão de término: ${hora}.`;
}
